import Phaser from 'phaser';
import { INVENTIONS, type Invention } from '../data/inventions';
import { recordResolve } from '../state/scoreboard';

const REVEALED_KEY = 'pa-revealed';
const STATUS_KEY = 'pa-revealed-status';

type RevealStatus = 'win' | 'gave-up';

function loadRevealedStatus(): Record<string, RevealStatus> {
  try {
    const raw = localStorage.getItem(STATUS_KEY);
    if (raw) return JSON.parse(raw) as Record<string, RevealStatus>;
  } catch {}
  return {};
}
function saveRevealedStatus(s: Record<string, RevealStatus>) {
  try { localStorage.setItem(STATUS_KEY, JSON.stringify(s)); } catch {}
}

const STATE_TINT: Record<string, number> = {
  WA: 0xa54838, NT: 0x8c3024, QLD: 0x4a7634, SA: 0x986238,
  NSW: 0x3e6638, VIC: 0x3a5a5a, TAS: 0x254842, ACT: 0x6e8a4a,
};

const TYPE_COLOUR: Record<string, number> = {
  health: 0xc94f3e, science: 0x4a8db8, consumer: 0xe6a544,
  tech: 0x6c4ab1, industrial: 0x6e6e6e, agri: 0x6b8c3a,
  defence: 0x4a3e2a, culture: 0xb19b6e,
};

// Fuzzy match for the riddle answer — accepts canonical name, puzzleName,
// and any alias (lowercase, punctuation stripped).
function normaliseAnswer(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
}
function matchesInvention(guess: string, inv: Invention): boolean {
  const g = normaliseAnswer(guess);
  if (!g) return false;
  // Also compare with spaces removed so 'pace maker' matches 'pacemaker'
  // and vice versa, without needing both as explicit aliases.
  const gFlat = g.replace(/\s+/g, '');
  const eq = (target: string) => {
    const t = normaliseAnswer(target);
    return t === g || t.replace(/\s+/g, '') === gFlat;
  };
  if (eq(inv.name)) return true;
  if (eq(inv.puzzleName)) return true;
  for (const a of inv.aliases) {
    if (eq(a)) return true;
  }
  return false;
}

function loadRevealed(): Set<string> {
  try {
    const raw = localStorage.getItem(REVEALED_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {}
  return new Set();
}
function saveRevealed(set: Set<string>) {
  try { localStorage.setItem(REVEALED_KEY, JSON.stringify([...set])); } catch {}
}

interface RiddleSceneData {
  inventionId: string;
}

export class RiddleScene extends Phaser.Scene {
  private invention!: Invention;
  private cardContainer?: Phaser.GameObjects.Container;
  private cardBack?: Phaser.GameObjects.Container;
  private cardFront?: Phaser.GameObjects.Container;
  private riddleText?: Phaser.GameObjects.Text;
  private feedback?: Phaser.GameObjects.Text;
  private inputContainer?: HTMLDivElement;
  private gaveUp = false;
  private guessCount = 0;       // counts the winning guess too — solved on 1st try => 1
  // Card dimensions, used by both back and front so they match through the flip.
  private cardW = 0;
  private cardH = 0;
  private cardCenterY = 0;

  constructor() {
    super({ key: 'Riddle', active: false });
  }

  init(data: RiddleSceneData) {
    const found = INVENTIONS.find((i) => i.id === data.inventionId);
    if (!found) {
      this.scene.stop();
      return;
    }
    this.invention = found;
  }

  create() {
    const W = this.game.config.width as number;
    const H = this.game.config.height as number;
    const narrow = W < 1100;

    // Semi-transparent backdrop — keeps the map visible behind so the
    // riddle still feels anchored to the continent. Interactive (no-op) so
    // it eats clicks instead of letting them fall through to the map tiles
    // underneath, but it does NOT close on click — closing is via the
    // explicit "back to map" link top-left.
    this.add.rectangle(W / 2, H / 2, W, H, 0x0d0a08, 0.85)
      .setInteractive();

    // Hide ContinentScene chrome (title, subline, CTA, stash button) so they
    // don't bleed through the translucent backdrop. Restore on shutdown.
    const continent = this.scene.get('Continent') as Phaser.Scene & {
      setChromeVisible?: (v: boolean) => void;
    };
    continent?.setChromeVisible?.(false);
    this.events.once('shutdown', () => {
      continent?.setChromeVisible?.(true);
    });

    // Top-left: close link
    const closeBtn = this.add.text(narrow ? 24 : 30, narrow ? 60 : 36, '✕  back to map', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '24px' : '17px', color: '#b6a079',
    }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#f2e5c0'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#b6a079'));
    closeBtn.on('pointerup', () => this.closeWithoutUnlock());

    // (No top-right counter — it would render stale, since the riddle
    // scene is created before the player solves and never refreshes.
    // The map HUD shows the live count after they exit.)

    // ---- Card hero — substantial size so it reads as the centerpiece in
    // both the riddle (?) state and the success (full info) state. Card
    // dimensions are cached on the scene so buildFront() uses the same.
    const cardW = narrow ? 360 : 320;
    const cardH = narrow ? 480 : 420;
    // cardY puts the composition centre near canvas mid-height. Card sits
    // in the upper portion; riddle + input pill stack beneath.
    const cardY = Math.round(H * 0.42);
    this.cardW = cardW;
    this.cardH = cardH;
    this.cardCenterY = cardY;

    this.cardContainer = this.add.container(W / 2, cardY);

    // Back face — same playing-card-style as the Pokédex
    this.cardBack = this.add.container(0, 0);
    // Interactive (no-op) so clicks on the card itself are swallowed
    // instead of falling through to the dim backdrop (which closes).
    const backBg = this.add.rectangle(0, 0, cardW, cardH, 0xa83320)
      .setStrokeStyle(4, 0xffd966)
      .setInteractive()
      .on('pointerup', (_p: any, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
        e.stopPropagation();
      });
    this.cardBack.add(backBg);
    this.cardBack.add(this.add.rectangle(0, 0, cardW - 16, cardH - 16, 0xa83320)
      .setStrokeStyle(1, 0xffd966).setFillStyle(0xa83320, 0));
    this.cardBack.add(this.add.text(0, -10, '?', {
      fontFamily: 'Fraunces, Georgia, serif',
      fontSize: narrow ? '200px' : '150px',
      color: '#ffd966', fontStyle: 'italic',
    }).setOrigin(0.5));
    this.cardBack.add(this.add.text(0, cardH / 2 - (narrow ? 36 : 28), 'WHAT AM I?', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '22px' : '17px', color: '#ffd966',
    }).setOrigin(0.5));
    this.cardContainer.add(this.cardBack);

    // Front face — built lazily on solve
    this.cardFront = this.add.container(0, 0);
    this.cardFront.setVisible(false);
    this.cardContainer.add(this.cardFront);

    // ---- Riddle line directly below the card ------------------------
    const riddleY = cardY + cardH / 2 + (narrow ? 36 : 28);
    this.riddleText = this.add.text(W / 2, riddleY, this.invention.riddle, {
      fontFamily: 'Fraunces, Georgia, serif',
      fontSize: narrow ? '24px' : '26px',
      color: '#ead5ac', fontStyle: 'italic',
      wordWrap: { width: Math.min(W - 60, 640) }, align: 'center',
      lineSpacing: 6,
    }).setOrigin(0.5, 0); // top-anchored so we can compute the bottom y reliably

    // ---- DOM input pill — positioned just under the riddle text -----
    this.mountInput();

    // ---- Feedback line — sits below where the input pill renders ----
    // (Computed in CSS by the DOM, so we leave a generous gap and feedback
    // text floats below the pill area in canvas-space.)
    const feedbackY = riddleY + (narrow ? 220 : 160);
    this.feedback = this.add.text(W / 2, feedbackY, '', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '22px' : '17px', color: '#ffd966',
    }).setOrigin(0.5);

    // Give-up link is mounted as part of the DOM input pill so it sits
    // right under the riddle / answer box (see mountInput).
  }

  // ----------------------------------------------------------- input

  private mountInput() {
    if (this.inputContainer) return;
    const W = this.game.config.width as number;
    const H = this.game.config.height as number;
    const narrow = W < 1100;
    const canvas = this.game.canvas;

    const wrap = document.createElement('div');
    wrap.style.position = 'fixed';
    wrap.style.left = '50%';
    wrap.style.transform = 'translateX(-50%)';
    wrap.style.display = 'flex';
    wrap.style.flexDirection = 'column';
    wrap.style.alignItems = 'center';
    wrap.style.gap = narrow ? '10px' : '6px';
    wrap.style.zIndex = '60';
    wrap.style.background = 'rgba(26,23,19,0.97)';
    wrap.style.padding = narrow ? '10px 12px' : '8px 12px';
    wrap.style.border = '1px solid #b19b6e';
    wrap.style.boxShadow = '0 8px 24px rgba(0,0,0,0.5)';

    // Inner row: input + guess button live side-by-side.
    const row = document.createElement('div');
    row.style.display = 'flex';
    row.style.gap = narrow ? '8px' : '6px';

    // Position the pill just under the riddle text (in CSS pixels).
    // We compute a target canvas-Y and convert to viewport coords using the
    // current canvas bounding rect.
    const positionPill = () => {
      const r = canvas.getBoundingClientRect();
      const cssScale = r.height / H;
      const riddleBottomNative = (this.riddleText?.y ?? 0) + (this.riddleText?.height ?? 0);
      const desiredTopCSS = r.top + (riddleBottomNative + (narrow ? 28 : 18)) * cssScale;
      // Don't let the pill end up below the bottom of the visible viewport on
      // small screens — clamp so it stays visible.
      const maxTop = window.innerHeight - 90;
      wrap.style.top = `${Math.min(desiredTopCSS, maxTop)}px`;
    };

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'what am I?';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.style.background = '#0d0a08';
    input.style.color = '#f2e5c0';
    input.style.border = '1px solid #4a3e2a';
    input.style.padding = narrow ? '10px 12px' : '8px 12px';
    // Keep narrow at 16px — anything smaller triggers iOS Safari to auto-zoom.
    input.style.font = `${narrow ? '16px' : '18px'} 'JetBrains Mono', ui-monospace, monospace`;
    input.style.width = narrow ? '52vw' : '260px';
    input.style.outline = 'none';

    const btn = document.createElement('button');
    btn.textContent = 'guess';
    btn.style.background = '#a83320';
    btn.style.color = '#ffd966';
    btn.style.border = '1px solid #ffd966';
    btn.style.padding = narrow ? '10px 16px' : '8px 16px';
    btn.style.font = `${narrow ? '15px' : '18px'} 'JetBrains Mono', ui-monospace, monospace`;
    btn.style.cursor = 'pointer';
    btn.style.fontWeight = 'bold';

    const submit = () => {
      const v = input.value.trim();
      if (!v) return;
      this.guessCount += 1;
      if (matchesInvention(v, this.invention)) {
        this.handleCorrect();
      } else {
        this.handleWrong(v);
        input.value = '';
        input.focus();
      }
    };
    btn.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
      if (e.key === 'Escape') this.closeWithoutUnlock();
    });

    row.appendChild(input);
    row.appendChild(btn);
    wrap.appendChild(row);

    // Small "reveal the answer" link directly under the input row.
    // Reads as a learning action, not a failure — the card still gets
    // added to the cabinet either way (with a quiet score penalty).
    const giveUp = document.createElement('button');
    giveUp.textContent = 'reveal the answer';
    giveUp.style.background = 'transparent';
    giveUp.style.color = '#8a7a5e';
    giveUp.style.border = 'none';
    giveUp.style.padding = '2px 6px';
    giveUp.style.font = `${narrow ? '12px' : '14px'} 'JetBrains Mono', ui-monospace, monospace`;
    giveUp.style.cursor = 'pointer';
    giveUp.style.textDecoration = 'underline';
    giveUp.addEventListener('mouseover', () => { giveUp.style.color = '#b6a079'; });
    giveUp.addEventListener('mouseout', () => { giveUp.style.color = '#8a7a5e'; });
    giveUp.addEventListener('click', () => this.handleGiveUp());
    wrap.appendChild(giveUp);

    document.body.appendChild(wrap);
    this.inputContainer = wrap;
    positionPill();
    // Auto-focus only on wide canvases. On mobile, auto-focus pops the
    // on-screen keyboard immediately and covers the riddle — let the
    // player read first, tap the input when ready.
    if (!narrow) setTimeout(() => input.focus(), 50);

    window.addEventListener('resize', positionPill);
    this.events.once('shutdown', () => {
      window.removeEventListener('resize', positionPill);
      this.unmountInput();
    });
  }

  private unmountInput() {
    this.inputContainer?.remove();
    this.inputContainer = undefined;
  }

  // ----------------------------------------------------------- handlers

  private handleWrong(guess: string) {
    this.feedback?.setText(`"${guess}" — not quite. Try again.`).setColor('#ff7a5a');
    if (this.cardContainer) {
      this.tweens.add({
        targets: this.cardContainer, x: '+=10',
        duration: 50, yoyo: true, repeat: 3, ease: 'Sine.InOut',
      });
    }
  }

  private handleCorrect() {
    this.feedback?.setText('').setColor('#88ff66');
    this.unmountInput();
    this.markRevealed('win');
    recordResolve(this.invention.id, this.guessCount, /*gaveUp*/ false);
    this.flipCardToFront(/*won*/ true);
  }

  private handleGiveUp() {
    this.gaveUp = true;
    this.feedback?.setText(`it was ${this.invention.name}`).setColor('#b19b6e');
    this.unmountInput();
    this.markRevealed('gave-up');
    recordResolve(this.invention.id, this.guessCount, /*gaveUp*/ true);
    this.flipCardToFront(/*won*/ false);
  }

  private markRevealed(status: RevealStatus) {
    const set = loadRevealed();
    set.add(this.invention.id);
    saveRevealed(set);
    const statusMap = loadRevealedStatus();
    // Don't downgrade a previous win to a gave-up if the player replays.
    if (statusMap[this.invention.id] !== 'win') {
      statusMap[this.invention.id] = status;
      saveRevealedStatus(statusMap);
    }
  }

  private closeWithoutUnlock() {
    this.unmountInput();
    this.scene.stop();
  }

  // ----------------------------------------------------------- card flip

  private flipCardToFront(won: boolean) {
    if (!this.cardContainer || !this.cardBack || !this.cardFront) return;
    this.buildFront();
    this.tweens.add({
      targets: this.cardContainer, scaleX: 0, duration: 200, ease: 'Quad.In',
      onComplete: () => {
        this.cardBack?.setVisible(false);
        this.cardFront?.setVisible(true);
        this.tweens.add({
          targets: this.cardContainer!, scaleX: 1, duration: 240, ease: 'Back.Out',
          onComplete: () => this.showWinChrome(won),
        });
      },
    });
  }

  private buildFront() {
    if (!this.cardFront) return;
    const W = this.game.config.width as number;
    const narrow = W < 1100;
    const cardW = this.cardW;
    const cardH = this.cardH;

    const inv = this.invention;
    const tint = STATE_TINT[inv.origin] ?? 0x4a3e2a;

    // Outer fill + outer stroke
    this.cardFront.add(this.add.rectangle(0, 0, cardW, cardH, tint)
      .setStrokeStyle(3, 0xb19b6e)
      .setInteractive()
      .on('pointerup', (_p: any, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
        e.stopPropagation();
      }));
    // Inner hairline — gives the card a crafted, museum-card feel.
    this.cardFront.add(this.add.rectangle(0, 0, cardW - 14, cardH - 14, tint)
      .setStrokeStyle(1, 0x0d0a08, 0.35).setFillStyle(tint, 0));

    // Sprite — anchored to top with proper padding.
    const padTop = narrow ? 36 : 28;
    const spriteScale = narrow ? 3.6 : 3.0;
    const spriteSize = 64 * spriteScale;
    const spriteCenterY = -cardH / 2 + padTop + spriteSize / 2;

    const spriteKey = `inv-${inv.id}`;
    if (this.textures.exists(spriteKey)) {
      this.cardFront.add(this.add.image(0, spriteCenterY, spriteKey).setScale(spriteScale));
    } else {
      this.cardFront.add(this.add.text(0, spriteCenterY, '?', {
        fontFamily: 'Fraunces', fontSize: narrow ? '90px' : '70px', color: '#5a4e3a',
      }).setOrigin(0.5));
    }

    // Name — italic display, anchored just below the sprite.
    const nameTopY = spriteCenterY + spriteSize / 2 + (narrow ? 24 : 20);
    this.cardFront.add(this.add.text(0, nameTopY, inv.name, {
      fontFamily: 'Fraunces, Georgia, serif',
      fontSize: narrow ? '32px' : '34px',
      color: '#f2e5c0', fontStyle: 'italic',
      wordWrap: { width: cardW - 36 }, align: 'center',
      lineSpacing: 3,
    }).setOrigin(0.5, 0));

    // ---- Bottom-anchored stack: chip → inventor → year/state ----------
    // Chip pinned to bottom margin.
    const chipColour = TYPE_COLOUR[inv.type] ?? 0x4a3e2a;
    const chipW = narrow ? 110 : 90;
    const chipH = narrow ? 28 : 22;
    const chipBottomMargin = narrow ? 22 : 18;
    const chipCenterY = cardH / 2 - chipBottomMargin - chipH / 2;
    this.cardFront.add(this.add.rectangle(0, chipCenterY, chipW, chipH, chipColour, 0.95)
      .setStrokeStyle(1, 0x0d0a08, 0.35));
    this.cardFront.add(this.add.text(0, chipCenterY, inv.type, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '20px' : '15px', color: '#0d0a08',
    }).setOrigin(0.5));

    // Inventor — small secondary line, sits above chip.
    const inventorY = chipCenterY - chipH / 2 - (narrow ? 18 : 14);
    this.cardFront.add(this.add.text(0, inventorY, inv.inventor, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '20px' : '14px', color: '#b19b6e',
      wordWrap: { width: cardW - 32 }, align: 'center', lineSpacing: 2,
    }).setOrigin(0.5, 1));

    // Year · State — primary meta line, sits above inventor.
    const yearStateY = inventorY - (narrow ? 30 : 22);
    this.cardFront.add(this.add.text(0, yearStateY, `${inv.year}  ·  ${inv.origin}`, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '17px' : '18px', color: '#ead5ac',
    }).setOrigin(0.5, 1));
  }

  /**
   * Success state — runs after the card flips to the front. Lays out the
   * blurb / "did you know?" / unlock confirmation / next button below the
   * card with a cursor-Y rhythm so each section gets its own breathing
   * room. Hides the riddle question and the give-up link.
   */
  private showWinChrome(won: boolean) {
    const W = this.game.config.width as number;
    const H = this.game.config.height as number;
    const narrow = W < 1100;
    const cardH = this.cardH;
    const cardY = this.cardCenterY;

    // Hide pre-success chrome that no longer applies. (The give-up link
    // lives inside the DOM input pill which was already unmounted in
    // handleCorrect / handleGiveUp.)
    this.riddleText?.destroy();
    this.riddleText = undefined;
    this.feedback?.destroy();
    this.feedback = undefined;

    // ---- Vertical rhythm tokens ---------------------------------------
    const t = narrow
      ? {
          gapCardToBlurb: 56,
          gapBlurbToDivider: 32,
          dividerW: 240,
          gapDividerToEyebrow: 24,
          gapEyebrowToFact: 12,
          gapFactToUnlock: 36,
          gapUnlockToBtn: 26,
          blurbSize: '24px',
          blurbLineSpacing: 7,
          eyebrowSize: '20px',
          factSize: '22px',
          factLineSpacing: 6,
          unlockSize: '20px',
          btnSize: '26px',
          btnPadX: 32,
          btnPadY: 16,
        }
      : {
          gapCardToBlurb: 40,
          gapBlurbToDivider: 24,
          dividerW: 180,
          gapDividerToEyebrow: 18,
          gapEyebrowToFact: 8,
          gapFactToUnlock: 26,
          gapUnlockToBtn: 20,
          blurbSize: '24px',
          blurbLineSpacing: 7,
          eyebrowSize: '14px',
          factSize: '18px',
          factLineSpacing: 5,
          unlockSize: '15px',
          btnSize: '24px',
          btnPadX: 32,
          btnPadY: 15,
        };

    const wrapW = Math.min(W - 80, narrow ? 720 : 580);
    let cursorY = cardY + cardH / 2 + t.gapCardToBlurb;

    // ---- Blurb (italic Fraunces, the editorial voice) ----------------
    const blurb = this.add.text(W / 2, cursorY, this.invention.blurb, {
      fontFamily: 'Fraunces, Georgia, serif',
      fontSize: t.blurbSize,
      color: '#ead5ac', fontStyle: 'italic',
      wordWrap: { width: wrapW }, align: 'center',
      lineSpacing: t.blurbLineSpacing,
    }).setOrigin(0.5, 0);
    cursorY += blurb.height + t.gapBlurbToDivider;

    // ---- Hairline divider — anchors the eye for the section break ----
    this.add.rectangle(W / 2, cursorY, t.dividerW, 1, 0x4a3e2a);
    cursorY += t.gapDividerToEyebrow;

    // ---- "DID YOU KNOW?" eyebrow — tracked, gold accent --------------
    this.add.text(W / 2, cursorY, 'D I D   Y O U   K N O W ?', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: t.eyebrowSize,
      color: '#ffd966',
    }).setOrigin(0.5, 0);
    cursorY += parseInt(t.eyebrowSize, 10) + t.gapEyebrowToFact;

    // ---- Fact body ---------------------------------------------------
    const fact = this.add.text(W / 2, cursorY, this.invention.fact, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: t.factSize,
      color: '#f2e5c0',
      wordWrap: { width: wrapW }, align: 'center',
      lineSpacing: t.factLineSpacing,
    }).setOrigin(0.5, 0);
    cursorY += fact.height + t.gapFactToUnlock;

    // ---- Unlock confirmation -----------------------------------------
    const unlockTxt = won
      ? '✓  added to the shelf'
      : '✓  revealed · added to the shelf';
    this.add.text(W / 2, cursorY, unlockTxt, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: t.unlockSize,
      color: won ? '#88ff66' : '#b6a079',
    }).setOrigin(0.5, 0);
    cursorY += parseInt(t.unlockSize, 10) + t.gapUnlockToBtn;

    // ---- Next button — anchored to flow OR to bottom (whichever fits)
    const targetBtnY = Math.min(cursorY, H - (narrow ? 100 : 70));
    const nextBtn = this.add.text(W / 2, targetBtnY, '▸  next  ▸', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: t.btnSize,
      color: '#0d0a08', backgroundColor: '#ffd966',
      padding: { x: t.btnPadX, y: t.btnPadY },
    }).setOrigin(0.5, 0).setInteractive({ useHandCursor: true });
    nextBtn.on('pointerover', () => nextBtn.setBackgroundColor('#fff5c0'));
    nextBtn.on('pointerout', () => nextBtn.setBackgroundColor('#ffd966'));
    nextBtn.on('pointerup', () => {
      this.scene.stop();
      const continent = this.scene.get('Continent') as Phaser.Scene & {
        onRiddleResolved?: (id: string) => void;
      };
      if (continent && typeof continent.onRiddleResolved === 'function') {
        continent.onRiddleResolved(this.invention.id);
      }
    });

    if (won && !this.gaveUp) this.spawnConfetti(cardY, cardH);
  }

  private spawnConfetti(centerY: number, _cardH: number) {
    const W = this.game.config.width as number;
    const colours = [0xffd966, 0xa83320, 0x88ff66, 0x4a8db8, 0xe6a544];
    for (let i = 0; i < 50; i++) {
      const x = Phaser.Math.Between(W * 0.25, W * 0.75);
      const y = centerY;
      const colour = colours[i % colours.length];
      const r = this.add.rectangle(x, y, Phaser.Math.Between(4, 9),
        Phaser.Math.Between(8, 14), colour);
      r.setDepth(200);
      this.tweens.add({
        targets: r,
        y: y + Phaser.Math.Between(180, 360),
        x: x + Phaser.Math.Between(-160, 160),
        angle: Phaser.Math.Between(-540, 540),
        alpha: { from: 1, to: 0 },
        duration: Phaser.Math.Between(900, 1500),
        ease: 'Sine.Out',
        onComplete: () => r.destroy(),
      });
    }
  }
}
